import {
  FeatureServiceBinder,
  FeatureServiceProviderDefinition,
  FeatureServices,
  SharedFeatureService,
} from '@feature-hub/core';
import {ConsumerLocation, HistoryServiceV2} from '@feature-hub/history-service';
import {
  NavigationServiceOptions,
  NavigationServiceV1,
} from '@volkswagen-onehub/navigation-service';

export interface NavigationServiceDependencies extends FeatureServices {
  readonly 's2:history': HistoryServiceV2;
}

export interface SharedNavigationService extends SharedFeatureService {
  readonly '1.1.0': FeatureServiceBinder<NavigationServiceV1>;
}

// This does basically the same as the original navigation service. But instead
// of pushing and replacing with the history, window.location.assign and
// window.location.replace is used to load the whole page again so that a mode
// change is fully applied in the integrator.
export function defineNavigationService(
  options: NavigationServiceOptions,
): FeatureServiceProviderDefinition<
  SharedNavigationService,
  NavigationServiceDependencies
> {
  return {
    id: 'navigation-service',

    dependencies: {
      featureServices: {
        's2:history': '^2.0.0',
      },
    },

    create: (env) => {
      const {'s2:history': historyService} = env.featureServices;
      const {rootHistory} = historyService;
      const params = new URLSearchParams(rootHistory.location.search);

      params.delete('-');

      // Does nothing but break the bfcache of firefox and safari
      if (typeof window !== 'undefined') {
        window.addEventListener('unload', () => undefined);
      }

      const featureService: NavigationServiceV1 = {
        navigateTo: (pageName, locationsByConfigId, anchor) => {
          const page = options.pages[pageName];

          if (!page) {
            return undefined;
          }

          const featureAppConsumerLocations: ConsumerLocation[] = [];

          for (const [configId, locationOrPathname] of Object.entries(
            locationsByConfigId,
          )) {
            const historyKey = page.featureApps[configId];

            if (!historyKey) {
              continue;
            }

            const location =
              typeof locationOrPathname === 'string'
                ? {pathname: locationOrPathname}
                : locationOrPathname;

            featureAppConsumerLocations.push({historyKey, location});
          }

          return featureService.navigateByConsumerLocations(
            pageName,
            featureAppConsumerLocations,
            anchor,
          );
        },

        navigateByConsumerLocations: (
          pageName,
          featureAppConsumerLocations,
          anchor,
        ) => {
          const page = options.pages[pageName];

          if (!page) {
            throw new Error(
              `Could not find the page "${page}" in mock navigation service options.`,
            );
          }

          const rootLocation = historyService.createNewRootLocationForMultipleConsumers(
            ...featureAppConsumerLocations,
            {
              historyKey: options.primaryConsumerId,
              location: {
                pathname: page.path,
                search: params.toString(),
                hash: anchor,
              },
            },
          );

          const href = rootHistory.createHref(rootLocation);

          return {
            createHref: () => href,
            push: () => window.location.assign(href),
            replace: () => window.location.replace(href),
          };
        },
      };

      return {'1.1.0': () => ({featureService})};
    },
  };
}
